























import { canEditAppointmentOutcome } from '@/components/appointments/appointment-utilities';
import appointmentsTable from '@/components/appointments/appointments-table.vue';
import layout from '@/layouts/app.vue';
import { Appointment } from '@/shared/appointments/appointment';
import { AppointmentOutcomeType } from '@/shared/types/appointment-outcome-type';
import { User } from '@/shared/user';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const userModule = namespace('user');
const typesModule = namespace('types');

@Component({
    components: {
        layout,
        appointmentsTable,
    },
})
export default class AppointmentList extends Vue {
    @typesModule.Getter('appointmentOutcomeTypes') public outcomeTypes!: Array<AppointmentOutcomeType>;

    @userModule.Getter('user') private currentUser!: User | null;
    @userModule.Getter('isAdministrator') private isAdministrator!: boolean;
    @userModule.Getter('isInternalKeyStakeholder') private isInternalKeyStakeholder!: boolean;
    @userModule.Getter('isCustomerExperienceExecutive') private isCustomerExperienceExecutive!: boolean;
    @userModule.Getter('isCustomerExperienceExecutiveTeamLeader') private isCustomerExperienceExecutiveTeamLeader!: boolean;
    @userModule.Getter('isHeadOfDepartment') private isHeadOfDepartment!: boolean;
    @userModule.Getter('isLegalAdvisor') private isLegalAdvisor!: boolean;
    @userModule.Getter('isLegalAssistant') private isLegalAssistant!: boolean;
    @userModule.Getter('isDataCreationExecutive') private isDataCreationExecutive!: boolean;
    @userModule.Getter('isProbateLegalAdvisor') private isProbateLegalAdvisor!: boolean;

    @Prop({ required: true }) public appointments!: Array<Appointment>;

    private isLegalAdvisorAt(partnerPracticeId: string | null): boolean {
        return this.isLegalAdvisor && this.currentUser!.partnerPracticeId === partnerPracticeId;
    }

    private isLegalAssistantAt(partnerPracticeId: string | null): boolean {
        return this.isLegalAssistant && this.currentUser!.partnerPracticeId === partnerPracticeId;
    }

    private isHeadOfDepartmentAt(partnerPracticeId: string | null): boolean {
        return this.isHeadOfDepartment && this.currentUser!.partnerPracticeId === partnerPracticeId;
    }

    private canCreateOutcome(appointment: Appointment): boolean {
        const partnerPracticeId = appointment.clients[0].partnerPracticeId;

        const validUser = this.currentUser != null &&
            (
                this.isInternalKeyStakeholder ||
                (this.isLegalAdvisorAt(partnerPracticeId) && this.currentUser.id === appointment.legalAdvisorId) ||
                (this.isLegalAssistantAt(partnerPracticeId) && this.currentUser.id === appointment.legalAdvisorId) ||
                this.isHeadOfDepartmentAt(partnerPracticeId)
            );

        return validUser && appointment.id != null && appointment.outcome == null && !appointment.isRebooked;
    }

    private canEditOutcome(appointment: Appointment): boolean {
        const partnerPracticeId = appointment.clients[0].partnerPracticeId;

        const validUser = this.currentUser != null &&
            (
                this.isInternalKeyStakeholder ||
                (this.isLegalAdvisorAt(partnerPracticeId) && this.currentUser.id === appointment.legalAdvisorId) ||
                (this.isLegalAssistantAt(partnerPracticeId) && this.currentUser.id === appointment.legalAdvisorId) ||
                this.isHeadOfDepartmentAt(partnerPracticeId)
            );

        return validUser && canEditAppointmentOutcome(appointment);
    }

    private canRebookAppointment(appointment: Appointment): boolean {
        const partnerPracticeId = appointment.clients[0].partnerPracticeId;

        const validUser = this.currentUser != null &&
            (
                this.isInternalKeyStakeholder ||
                this.isCustomerExperienceExecutive ||
                this.isCustomerExperienceExecutiveTeamLeader ||
                this.isLegalAdvisorAt(partnerPracticeId) ||
                this.isLegalAssistantAt(partnerPracticeId) ||
                this.isHeadOfDepartmentAt(partnerPracticeId) ||
                this.isDataCreationExecutive ||
                this.isProbateLegalAdvisor
            );

        if (appointment.outcome == null) {
            return validUser && appointment.id != null && !appointment.isRebooked;
        }

        const outcomeType = this.outcomeTypes.find((type) => type.id === appointment.outcome!.outcomeTypeId);

        if (outcomeType == null) {
            return false;
        }

        return validUser && appointment.id != null && !appointment.isRebooked && !outcomeType.isSat;
    }

    private canEditAppointment(appointment: Appointment): boolean {
        return !appointment.isRebooked;
    }

    private canDeleteAppointment(): boolean {
        return this.isAdministrator;
    }

    private canDeleteOutcome(appointment: Appointment): boolean {
        return this.isAdministrator && appointment.outcome != null;
    }

    private async showViewDialog(appointment: Appointment): Promise<void> {
        this.$emit('show-view-dialog', appointment);
    }

    private async showCompletionDialog(appointment: Appointment): Promise<void> {
        this.$emit('show-completion-dialog', appointment);
    }

    private async showEditOutcomeDialog(appointment: Appointment): Promise<void> {
        this.$emit('show-edit-outcome-dialog', appointment);
    }

    private async showRebookDialog(appointment: Appointment): Promise<void> {
        this.$emit('show-rebook-dialog', appointment);
    }

    private async showEditDialog(appointment: Appointment): Promise<void> {
        this.$emit('show-edit-dialog', appointment);
    }

    private async showDeleteAppointmentConfirmation(appointment: Appointment): Promise<void> {
        this.$emit('show-delete-appointment-dialog', appointment);
    }

    private async showDeleteOutcomeConfirmation(appointment: Appointment): Promise<void> {
        this.$emit('show-delete-outcome-dialog', appointment);
    }
}
